import { LitElement, html, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { hygieneColumns, resolvedBy } from '../lib/hygiene.js';
import { withFormats } from '../lib/table.js';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin, store } from '../store.js';
import { api } from '../api.js';
import { navigate } from '../router.js';
import {
  HYGIENE_STATUS_META,
  basisLabel,
  entityTypeLabel,
  evidenceLines,
  flagChips,
  flagMeta,
  inToolFix,
  owner as ownerOf,
  rankItems,
  sourceLink,
  statusMeta,
  worklistRows,
  SYSTEM_LABEL,
} from '../lib/hygiene.js';
import { hours as formatHours, count, money, mediumDate, percent, since } from '../lib/format.js';

/** The entity types, as the filter offers them. */
const ENTITY_TYPES = [
  { value: '', label: 'Everything' },
  { value: 'project', label: 'Projects' },
  { value: 'opportunity', label: 'Deals' },
  { value: 'taxonomy_value', label: 'Taxonomy values' },
];

const SORTS = [
  { value: 'hours', label: 'Hours at stake' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'newest', label: 'Newest first' },
  { value: 'flag', label: 'By problem' },
];

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 250;

/**
 * The data hygiene worklist (PRD 9.3, 10.10; V-18; Phase 34): every missing-data flag the last
 * run raised, ranked by the hours whose confidence it affects, with the person to ask, the
 * record to open, and the two decisions a planner can take about it.
 *
 * The screen exists to turn six percentages into assigned work. The metrics card beneath says
 * how much of the forecast is inferred; this card says *which* missing facts are doing it, in
 * the order that would buy back the most confidence, and it is the only place in the tool where
 * a reader can act on that.
 *
 * Every figure comes from `GET /quality/hygiene`. The component adds no arithmetic: the hours,
 * the ranking, the counts and the evidence are the run's own, and `lib/hygiene.js` adds only the
 * words — what each flag is called, what the fix is, and where the fix is made. That is what
 * makes the review gate a comparison rather than an audit: the top item on screen is the top row
 * of the endpoint, and its hours are the run's.
 *
 * **Two decisions, and they are not symmetrical.** *Resolved* is a claim — somebody says they
 * have fixed it in Workamajig or HubSpot — and the next run either confirms it by not raising
 * the flag or reopens the item, so it costs nothing to be wrong and is written straight away.
 * *Dismissed* is the decision no run undoes, so it asks first, and the confirm carries an
 * optional note for the next person. Both are reversible by hand, and the line above the table
 * says what just happened with an Undo, because a row that silently vanished from a filtered
 * list would leave a planner unsure whether they clicked the one they meant.
 *
 * **A written row leaves the list.** The counts and the totals come back from the server after
 * every write rather than being patched here; an item is on the worklist or it is not, and a
 * screen that kept a dismissed row in an "open" list would be showing a list that does not
 * exist.
 */
class HygieneWorklist extends StoreMixin(LitElement) {
  static properties = {
    _list: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _writeError: { state: true },
    _ack: { state: true },
    _saving: { state: true },
    _expanded: { state: true },
    _confirming: { state: true },
    _note: { state: true },
    _status: { state: true },
    _flag: { state: true },
    _entityType: { state: true },
    _mine: { state: true },
    _q: { state: true },
    _sort: { state: true },
    _offset: { state: true },
    _tableOpen: { state: true },
  };

  constructor() {
    super();
    this._list = null;
    this._loading = false;
    this._error = null;
    this._writeError = null;
    this._ack = null;
    this._saving = new Set();
    this._expanded = new Set();
    this._confirming = null;
    this._note = '';
    // Open, because the worklist is the work in front of somebody; the other statuses are
    // history, and history is a filter away.
    this._status = 'open';
    this._flag = '';
    this._entityType = '';
    this._mine = false;
    this._q = '';
    this._sort = 'hours';
    this._offset = 0;
    this._tableOpen = false;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
      }
      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0 0 0 0);
        white-space: nowrap;
        border: 0;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-start;
        gap: 16px 24px;
        margin-bottom: 14px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 6px 0 4px;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
        max-width: 40ch;
      }
      .sub {
        color: var(--ink-muted);
        font-size: 13px;
        max-width: 72ch;
      }

      .kpis {
        margin-left: auto;
        display: flex;
        flex-wrap: wrap;
        gap: 8px 10px;
      }
      .kpi {
        border: 1px solid var(--border-soft);
        border-radius: 8px;
        background: var(--surface-2);
        padding: 8px 14px;
        min-width: 108px;
      }
      .kpi .value {
        font-size: 20px;
        font-weight: 700;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
        line-height: 1.15;
      }
      .kpi .label {
        font-size: 11.5px;
        color: var(--ink-muted);
      }

      /* Controls: the status segmented control, then the flag chips, then the rest. */
      .controls {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 12px;
        padding: 10px 0 12px;
        border-top: 1px solid var(--border-soft);
      }
      .segmented {
        display: inline-flex;
        border: 1px solid var(--control-border);
        border-radius: var(--radius-pill);
        background: var(--control-bg);
        padding: 2px;
      }
      .segmented button {
        font: inherit;
        font-size: 12.5px;
        font-weight: 600;
        color: var(--ink-muted);
        background: none;
        border: 0;
        border-radius: var(--radius-pill);
        padding: 4px 12px;
        cursor: pointer;
        white-space: nowrap;
      }
      .segmented button[aria-pressed='true'] {
        color: var(--surface);
        background: var(--ink-strong);
      }
      label.field {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      select,
      input[type='search'],
      input[type='text'] {
        font: inherit;
        font-size: 13px;
        color: var(--ink);
        background: var(--control-bg);
        border: 1px solid var(--control-border);
        border-radius: 6px;
        padding: 5px 8px;
      }
      input[type='search'] {
        min-width: 18ch;
      }
      .check {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      .clear {
        font: inherit;
        font-size: 12.5px;
        color: var(--link);
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;
        text-decoration: underline;
      }

      .chips {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        padding-bottom: 12px;
      }
      .chip {
        font: inherit;
        font-size: 12.5px;
        display: inline-flex;
        align-items: baseline;
        gap: 6px;
        border: 1px solid var(--control-border);
        background: var(--control-bg);
        color: var(--ink);
        border-radius: var(--radius-pill);
        padding: 3px 11px;
        cursor: pointer;
      }
      .chip[aria-pressed='true'] {
        background: var(--ink-strong);
        border-color: var(--ink-strong);
        color: var(--surface);
      }
      .chip .n {
        font-variant-numeric: tabular-nums;
        opacity: 0.75;
        font-size: 11.5px;
      }

      .notice {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 8px;
        font-size: 13px;
        border-radius: 8px;
        padding: 8px 12px;
        margin-bottom: 12px;
      }
      .notice.done {
        background: var(--status-healthy-tint);
        border: 1px solid var(--status-healthy);
        color: var(--ink);
      }
      .notice.error {
        background: var(--status-overloaded-tint);
        border: 1px solid var(--status-overloaded);
        color: var(--ink);
      }
      .notice button {
        font: inherit;
        font-size: 12.5px;
        font-weight: 600;
        background: none;
        border: 0;
        padding: 0;
        color: var(--link);
        text-decoration: underline;
        cursor: pointer;
      }

      .scroll {
        overflow-x: auto;
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
      }
      table {
        border-collapse: collapse;
        width: 100%;
        font-size: 13px;
      }
      th,
      td {
        text-align: left;
        padding: 9px 12px;
        border-bottom: 1px solid var(--border-soft);
        vertical-align: top;
      }
      thead th {
        background: var(--surface-2);
        font-family: var(--font-eyebrow);
        font-size: 10.5px;
        letter-spacing: 0.08em;
        text-transform: uppercase;
        color: var(--ink-muted);
        white-space: nowrap;
      }
      td.rank,
      th.rank {
        text-align: right;
        width: 1%;
        color: var(--ink-muted);
        font-variant-numeric: tabular-nums;
      }
      td.num,
      th.num {
        text-align: right;
        font-variant-numeric: tabular-nums;
        white-space: nowrap;
      }
      tbody tr.item:hover > td {
        background: var(--surface-2);
      }
      tr.item.written > td {
        opacity: 0.6;
      }

      .entity {
        display: flex;
        flex-direction: column;
        gap: 2px;
        min-width: 22ch;
      }
      .entity .name {
        font-weight: 600;
        color: var(--ink-strong);
      }
      /* A project's name leaves for its panel; an opportunity's and a taxonomy value's have
       nowhere of their own to go, so only the one that leads somewhere reads as a link. */
      .entity button.name {
        font: inherit;
        font-weight: 600;
        color: var(--link);
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;
        text-align: left;
      }
      .entity button.name:hover {
        text-decoration: underline;
      }
      .entity .meta {
        color: var(--ink-muted);
        font-size: 12px;
      }
      .missing {
        min-width: 18ch;
      }
      .missing .flag {
        font-weight: 600;
      }
      .why {
        font: inherit;
        font-size: 12px;
        color: var(--link);
        background: none;
        border: 0;
        padding: 0;
        margin-top: 2px;
        cursor: pointer;
        text-decoration: underline;
        display: block;
      }

      /* Hours at stake is the ranking, so the cell shows it as a share of the largest on the page
       as well as in figures. The number is always there; the bar is the second channel. */
      .stake {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        gap: 3px;
        min-width: 12ch;
      }
      .stake .value {
        font-weight: 700;
        color: var(--ink-strong);
      }
      .bar {
        width: 92px;
        height: 4px;
        border-radius: 2px;
        background: var(--surface-sunken);
        overflow: hidden;
      }
      .bar span {
        display: block;
        height: 100%;
        background: var(--ink-strong);
      }
      .stake .basis {
        font-size: 11px;
        color: var(--ink-muted);
        text-align: right;
        max-width: 22ch;
        white-space: normal;
      }

      .owner .name {
        font-weight: 600;
      }
      .owner .role {
        color: var(--ink-muted);
        font-size: 12px;
      }
      .owner .unmatched {
        color: var(--ink-faint);
        font-size: 11.5px;
      }

      .fix {
        min-width: 20ch;
      }
      .fix .verb {
        font-weight: 600;
      }
      .fix .where {
        color: var(--ink-muted);
        font-size: 12px;
      }
      a.link {
        color: var(--link);
        font-size: 12px;
      }
      /* The reference, linked: quiet in the meta line, a link on hover. */
      .entity .meta a.ref {
        color: inherit;
        text-decoration: underline;
        text-decoration-color: var(--border);
        text-underline-offset: 2px;
      }
      .entity .meta a.ref:hover {
        color: var(--link);
        text-decoration-color: currentColor;
      }

      .actions {
        display: flex;
        flex-wrap: wrap;
        gap: 6px;
        justify-content: flex-end;
        min-width: 15ch;
      }
      .actions button {
        font: inherit;
        font-size: 12.5px;
        color: var(--ink);
        background: var(--control-bg);
        border: 1px solid var(--control-border);
        border-radius: 6px;
        padding: 4px 10px;
        cursor: pointer;
        white-space: nowrap;
      }
      .actions button:hover:not([disabled]) {
        background: var(--surface-2);
      }
      .actions button[disabled] {
        opacity: 0.5;
        cursor: default;
      }
      .actions button.danger {
        border-color: var(--status-overloaded);
        color: var(--status-overloaded);
      }

      .badge {
        display: inline-block;
        border-radius: var(--radius-pill);
        border: 1px solid currentColor;
        padding: 1px 8px;
        font-size: 11.5px;
        font-weight: 600;
        white-space: nowrap;
      }
      .badge.resolved {
        color: var(--status-healthy);
        background: var(--status-healthy-tint);
      }
      .badge.dismissed {
        color: var(--status-underload);
        background: var(--status-underload-tint);
      }

      /* The evidence row and the dismissal confirm both hang beneath their item. */
      tr.panel > td {
        background: var(--surface-2);
        border-bottom: 1px solid var(--border);
      }
      .evidence {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
        gap: 8px 22px;
        margin: 0;
      }
      .evidence div {
        display: flex;
        flex-direction: column;
      }
      .evidence dt {
        font-size: 11px;
        color: var(--ink-muted);
        font-family: var(--font-eyebrow);
        letter-spacing: 0.06em;
        text-transform: uppercase;
      }
      .evidence dd {
        margin: 0;
        font-size: 13px;
        color: var(--ink);
      }
      .evidence ul {
        margin: 2px 0 0;
        padding-left: 18px;
        font-size: 12.5px;
        color: var(--ink);
      }
      .evidence .wide {
        grid-column: 1 / -1;
      }
      .weeks {
        width: auto;
        margin-top: 4px;
        font-size: 12.5px;
      }
      .weeks th,
      .weeks td {
        padding: 3px 10px 3px 0;
        border: 0;
      }
      .panel .why-text {
        margin: 0 0 10px;
        font-size: 13px;
        color: var(--ink-muted);
        max-width: 82ch;
      }
      .panel .seen {
        margin: 10px 0 0;
        font-size: 12px;
        color: var(--ink-muted);
      }
      .confirm {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 8px 12px;
        font-size: 13px;
      }
      .confirm strong {
        color: var(--ink-strong);
      }
      .confirm input[type='text'] {
        min-width: 28ch;
        flex: 1;
      }
      .confirm button {
        font: inherit;
        font-size: 12.5px;
        border-radius: 6px;
        padding: 5px 12px;
        cursor: pointer;
        border: 1px solid var(--control-border);
        background: var(--control-bg);
        color: var(--ink);
      }
      .confirm button.danger {
        background: var(--status-overloaded);
        border-color: var(--status-overloaded);
        color: #fff;
        font-weight: 600;
      }

      .state {
        padding: 28px 12px;
        text-align: center;
        color: var(--ink-muted);
        font-size: 13.5px;
      }
      .state.error {
        color: var(--status-overloaded);
      }

      .foot {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 10px;
        padding-top: 10px;
        font-size: 12.5px;
        color: var(--ink-muted);
      }
      .foot .pager {
        margin-left: auto;
        display: flex;
        gap: 6px;
        align-items: center;
      }
      .foot button {
        font: inherit;
        font-size: 12.5px;
        color: var(--ink);
        background: var(--control-bg);
        border: 1px solid var(--control-border);
        border-radius: 6px;
        padding: 4px 10px;
        cursor: pointer;
      }
      .foot button[disabled] {
        opacity: 0.5;
        cursor: default;
      }

      details.table {
        margin-top: 16px;
        border-top: 1px solid var(--border-soft);
        padding-top: 12px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 13px;
        font-weight: 600;
        color: var(--ink-strong);
      }
      details.table data-table {
        margin-top: 12px;
      }

      @media (max-width: 900px) {
        .kpis {
          margin-left: 0;
        }
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    // The worklist is not a property of a run — it outlives every one of them — but a run
    // reconciles it, so a run landing while the page is open is a reason to ask again.
    this._unsubscribe = store.subscribe(() => {
      const runId = this.appState.run?.id ?? null;
      if (runId === this._runId) return;
      this._runId = runId;
      this._load();
    });
    this._runId = this.appState.run?.id ?? null;
    this._load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribe?.();
    clearTimeout(this._searchTimer);
  }

  /** The query the filters describe. Only what differs from the endpoint's own defaults. */
  _params() {
    const params = { status: this._status, sort: this._sort, limit: PAGE_SIZE };
    if (this._offset) params.offset = this._offset;
    if (this._flag) params.flag = this._flag;
    if (this._entityType) params.entity_type = this._entityType;
    if (this._q.trim()) params.q = this._q.trim();
    if (this._mine && this.appState.user?.id) params.owner_user_id = this.appState.user.id;
    return params;
  }

  async _load() {
    const params = this._params();
    const key = JSON.stringify(params);
    this._requestKey = key;
    this._loading = true;
    this._error = null;
    try {
      const data = await api.get('/quality/hygiene', params);
      if (this._requestKey !== key) return; // a newer request has taken over
      this._list = data;
      // An item that is no longer on screen cannot be expanded or confirmed.
      const ids = new Set(data.items.map((i) => i.id));
      this._expanded = new Set([...this._expanded].filter((id) => ids.has(id)));
      if (this._confirming && !ids.has(this._confirming)) this._confirming = null;
      // The metrics card beneath compares the run's frozen snapshot with the worklist as it
      // stands; this is the only place that knows the second half of that comparison.
      this.dispatchEvent(
        new CustomEvent('hygiene-data', { detail: data, bubbles: true, composed: true }),
      );
    } catch (err) {
      if (this._requestKey !== key) return;
      this._error = `Could not load the worklist: ${err.message}`;
      this._list = null;
    } finally {
      if (this._requestKey === key) this._loading = false;
    }
  }

  /** A filter change is a new list, so it starts at the first page. */
  _setFilter(patch) {
    Object.assign(this, patch);
    this._offset = 0;
    this._ack = null;
    this._load();
  }

  _onSearch(value) {
    this._q = value;
    clearTimeout(this._searchTimer);
    this._searchTimer = setTimeout(() => {
      this._offset = 0;
      this._load();
    }, SEARCH_DEBOUNCE_MS);
  }

  _clearFilters() {
    clearTimeout(this._searchTimer);
    this._setFilter({
      _status: 'open',
      _flag: '',
      _entityType: '',
      _mine: false,
      _q: '',
      _sort: 'hours',
    });
  }

  get _filtered() {
    return (
      this._status !== 'open' ||
      !!this._flag ||
      !!this._entityType ||
      this._mine ||
      !!this._q.trim() ||
      this._sort !== 'hours'
    );
  }

  get _canWrite() {
    return ['planner', 'admin'].includes(this.appState.user?.effective_role);
  }

  get _isAdmin() {
    return this.appState.user?.effective_role === 'admin';
  }

  /** People by id, so an outlier week's evidence can name the person and not their uuid. */
  get _peopleById() {
    const people = this.appState.vocabularies?.people ?? [];
    if (this._peopleFor !== people) {
      this._people = new Map(people.map((p) => [p.id, p.display_name]));
      this._peopleFor = people;
    }
    return this._people;
  }

  _toggleExpanded(id) {
    const next = new Set(this._expanded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this._expanded = next;
  }

  /**
   * Resolve, dismiss or reopen one item. The list is reloaded from the server afterwards rather
   * than patched here: a written row usually no longer belongs to the filter it was read under,
   * and the counts beside every chip are the server's to state.
   *
   * What the reader keeps is the acknowledgement line, which names the row that left and offers
   * the way back — the item's previous status, undone by the same call.
   */
  async _setStatus(item, status, { note = null } = {}) {
    if (this._saving.has(item.id)) return;
    this._saving = new Set([...this._saving, item.id]);
    this._writeError = null;
    try {
      const res = await api.patch(`/quality/hygiene/${item.id}`, {
        status,
        ...(note ? { note } : {}),
      });
      this._confirming = null;
      this._note = '';
      this._ack = { item: res.item, from: item.status, to: status };
      await this._load();
    } catch (err) {
      this._writeError = `Could not update ${item.entity_label}: ${err.message}`;
    } finally {
      const next = new Set(this._saving);
      next.delete(item.id);
      this._saving = next;
    }
  }

  _startDismiss(item) {
    this._confirming = item.id;
    this._note = '';
    // The confirm is inside the row; the evidence has to be out of the way of it.
    const next = new Set(this._expanded);
    next.delete(item.id);
    this._expanded = next;
  }

  // -----------------------------------------------------------------------------------------
  // Rendering

  _kpis() {
    const list = this._list;
    const byStatus = list?.counts?.by_status ?? {};
    const openHours = (list?.counts?.by_flag ?? []).reduce(
      (sum, f) => sum + Number(f.open_hours_at_stake ?? 0),
      0,
    );
    const tiles = [
      { label: 'Open items', value: count(byStatus.open ?? 0) },
      { label: 'Hours at stake', value: formatHours(Math.round(openHours * 100) / 100) },
      { label: 'Resolved', value: count(byStatus.resolved ?? 0) },
      { label: 'Dismissed', value: count(byStatus.dismissed ?? 0) },
    ];
    return html`
      <div class="kpis">
        ${tiles.map(
          (t) => html`
            <div class="kpi">
              <div class="value">${t.value}</div>
              <div class="label">${t.label}</div>
            </div>
          `,
        )}
      </div>
    `;
  }

  _controls() {
    const chips = flagChips(this._list);
    return html`
      <div class="controls">
        <span class="segmented" role="group" aria-label="Status">
          ${[{ status: 'any', label: 'All' }, ...HYGIENE_STATUS_META].map(
            (s) => html`
              <button
                type="button"
                data-status=${s.status}
                title=${s.hint ?? ''}
                aria-pressed=${this._status === s.status ? 'true' : 'false'}
                @click=${() => this._setFilter({ _status: s.status })}
              >
                ${s.label}
              </button>
            `,
          )}
        </span>

        <label class="field">
          About
          <select
            .value=${this._entityType}
            @change=${(e) => this._setFilter({ _entityType: e.target.value })}
          >
            ${ENTITY_TYPES.map(
              (t) =>
                html`<option value=${t.value} ?selected=${this._entityType === t.value}>
                  ${t.label}
                </option>`,
            )}
          </select>
        </label>

        <label class="field">
          Order
          <select .value=${this._sort} @change=${(e) => this._setFilter({ _sort: e.target.value })}>
            ${SORTS.map(
              (s) =>
                html`<option value=${s.value} ?selected=${this._sort === s.value}>
                  ${s.label}
                </option>`,
            )}
          </select>
        </label>

        <label class="field">
          <span class="sr-only">Search</span>
          <input
            type="search"
            placeholder="Project, deal, client…"
            aria-label="Search the worklist"
            .value=${this._q}
            @input=${(e) => this._onSearch(e.target.value)}
          />
        </label>

        ${
          this.appState.user?.id
            ? html`
                <label class="check">
                  <input
                    type="checkbox"
                    .checked=${this._mine}
                    @change=${(e) => this._setFilter({ _mine: e.target.checked })}
                  />
                  Mine only
                </label>
              `
            : nothing
        }
        ${
          this._filtered
            ? html`<button class="clear" @click=${this._clearFilters}>Clear filters</button>`
            : nothing
        }
      </div>

      ${
        chips.length
          ? html`
              <div class="chips" role="group" aria-label="Problem">
                <button
                  class="chip"
                  type="button"
                  data-flag=""
                  aria-pressed=${this._flag === '' ? 'true' : 'false'}
                  @click=${() => this._setFilter({ _flag: '' })}
                >
                  Every problem
                </button>
                ${chips.map(
                  (c) => html`
                    <button
                      class="chip"
                      type="button"
                      data-flag=${c.flag}
                      title=${flagMeta(c.flag).why}
                      aria-pressed=${this._flag === c.flag ? 'true' : 'false'}
                      @click=${() => this._setFilter({ _flag: this._flag === c.flag ? '' : c.flag })}
                    >
                      ${c.label}
                      <span class="n"
                        >${count(c.open)} · ${formatHours(c.open_hours_at_stake)}</span
                      >
                    </button>
                  `,
                )}
              </div>
            `
          : nothing
      }
    `;
  }

  _notices() {
    const ack = this._ack;
    return html`
      ${
        this._writeError
          ? html`<div class="notice error" role="alert">${this._writeError}</div>`
          : nothing
      }
      ${
        ack
          ? html`
              <div class="notice done" data-ack=${ack.to} role="status">
                <span
                  ><strong>${statusMeta(ack.to).label}:</strong> ${ack.item.entity_label}
                  (${formatHours(ack.item.hours_at_stake)}). ${statusMeta(ack.to).hint}</span
                >
                ${
                  this._canWrite
                    ? html`<button
                        @click=${() => this._setStatus(ack.item, ack.from)}
                        ?disabled=${this._saving.has(ack.item.id)}
                      >
                        Undo
                      </button>`
                    : nothing
                }
              </div>
            `
          : nothing
      }
    `;
  }

  /** The largest hours figure on the page, which the bars are drawn as a share of. */
  _pageMax(items) {
    return items.reduce((max, i) => Math.max(max, Number(i.hours_at_stake ?? 0)), 0);
  }

  _row(item, pageMax) {
    const meta = flagMeta(item.flag);
    const who = ownerOf(item);
    const link = sourceLink(item);
    const inTool = inToolFix(item);
    const saving = this._saving.has(item.id);
    const expanded = this._expanded.has(item.id);
    const share = pageMax > 0 ? Math.max(2, (Number(item.hours_at_stake) / pageMax) * 100) : 0;

    return html`
      <tr class="item" data-id=${item.id} data-flag=${item.flag}>
        <td class="rank">${item.rank}</td>
        <td>
          <span class="entity">
            ${
              item.project_id
                ? html`<button
                    class="name"
                    type="button"
                    title="Open ${item.entity_label} in Project Intelligence"
                    @click=${() => navigate(`/projects/${item.project_id}`)}
                  >
                    ${item.entity_label}
                  </button>`
                : html`<span class="name">${item.entity_label}</span>`
            }
            <span class="meta">
              ${entityTypeLabel(item.entity_type)}${
                item.entity_reference
                  ? html` ·
                    ${
                      // The id is the record: a deal id opens the deal in HubSpot, a project
                      // number the project in Workamajig (once the URL template is set).
                      link
                        ? html`<a
                            class="ref"
                            href=${link.href}
                            target="_blank"
                            rel="noreferrer noopener"
                            title=${link.label}
                            >${item.entity_reference}</a
                          >`
                        : item.entity_reference
                    }`
                  : nothing
              }${item.client ? html` · ${item.client}` : nothing}
            </span>
            ${
              item.status !== 'open'
                ? html`<span class="meta"
                    ><span class="badge ${item.status}">${statusMeta(item.status).label}</span>
                    ${item.note ? html` ${item.note}` : nothing}</span
                  >`
                : nothing
            }
          </span>
        </td>
        <td class="missing">
          <span class="flag">${meta.missing}</span>
          <button
            class="why"
            type="button"
            aria-expanded=${expanded ? 'true' : 'false'}
            @click=${() => this._toggleExpanded(item.id)}
          >
            ${expanded ? 'Hide the evidence' : 'Why, and the evidence'}
          </button>
        </td>
        <td class="num">
          <span class="stake">
            <span class="value">${formatHours(item.hours_at_stake)}</span>
            <span class="bar" aria-hidden="true"><span style=${`width: ${share}%`}></span></span>
            <span class="basis">${basisLabel(item.hours_at_stake_basis)}</span>
          </span>
        </td>
        <td class="owner">
          ${
            who.name
              ? html`<span class="name">${who.name}</span>
                  <div class="role">${who.source_label}</div>
                  ${
                    who.unmatched
                      ? html`<div class="unmatched">Not a user of this tool</div>`
                      : nothing
                  }`
              : html`<span class="role">${who.source_label}</span>`
          }
        </td>
        <td class="fix">
          <span class="verb">${meta.fix}</span>
          <div class="where">in ${SYSTEM_LABEL[meta.where] ?? meta.where}</div>
          ${link ? html`<a class="link" href=${link.href} target="_blank" rel="noreferrer noopener">${link.label}</a>` : nothing}
          ${
            inTool && this._isAdmin
              ? html`<a class="link" href=${inTool.href}>${inTool.label}</a>`
              : nothing
          }
        </td>
        <td>
          <div class="actions">
            ${
              !this._canWrite
                ? nothing
                : html`
                    ${
                      item.status === 'open'
                        ? html`<button
                            ?disabled=${saving}
                            @click=${() => this._setStatus(item, 'resolved')}
                          >
                            Resolve
                          </button>`
                        : html`<button
                            ?disabled=${saving}
                            @click=${() => this._setStatus(item, 'open')}
                          >
                            Reopen
                          </button>`
                    }
                    ${
                      item.status === 'dismissed'
                        ? nothing
                        : html`<button
                            class="danger"
                            ?disabled=${saving}
                            @click=${() => this._startDismiss(item)}
                          >
                            Dismiss
                          </button>`
                    }
                  `
            }
          </div>
        </td>
      </tr>
      ${expanded ? this._evidenceRow(item, meta) : nothing}
      ${this._confirming === item.id ? this._confirmRow(item) : nothing}
    `;
  }

  _evidenceRow(item, meta) {
    const lines = evidenceLines(item, { peopleById: this._peopleById });
    return html`
      <tr class="panel" data-evidence=${item.id}>
        <td colspan="7">
          ${meta.why ? html`<p class="why-text">${meta.why}</p>` : nothing}
          <dl class="evidence">
            ${lines.map(
              (l) => html`
                <div class=${l.format === 'list' || l.format === 'outlier_weeks' ? 'wide' : ''}>
                  <dt>${l.label}</dt>
                  <dd>${this._evidenceValue(l)}</dd>
                </div>
              `,
            )}
          </dl>
          <p class="seen">
            First seen ${mediumDate(item.first_seen_at)}, last raised ${since(item.last_seen_at)}.
            ${
              item.status === 'resolved' && item.resolved_at
                ? html` Marked resolved
                  ${since(item.resolved_at)}${resolvedBy(item) ? ` ${resolvedBy(item)}` : ''}.`
                : nothing
            }
            ${item.note ? html` Note: ${item.note}` : nothing}
          </p>
        </td>
      </tr>
    `;
  }

  /** One evidence value, in the form its `format` asks for. */
  _evidenceValue(line) {
    const v = line.value;
    switch (line.format) {
      case 'hours':
        return formatHours(v);
      case 'count':
        return count(v);
      case 'money':
        return money(v);
      case 'percent':
        return percent(v);
      case 'date':
        return mediumDate(v);
      case 'date_range':
        return `${mediumDate(v.from)} – ${mediumDate(v.to)}`;
      case 'days':
        return `${count(v)} days`;
      case 'weeks':
        return `${count(v)} weeks`;
      case 'weekdays':
        return `${count(v)} weekdays`;
      case 'multiple':
        return `${v}× the person's contracted week`;
      case 'bool':
        return v ? 'Yes' : 'No';
      case 'list':
        return html`<ul>
          ${v.map((entry) => html`<li>${entry}</li>`)}
        </ul>`;
      case 'outlier_weeks':
        return html`
          <table class="weeks">
            <thead>
              <tr>
                <th>Week</th>
                <th>Who</th>
                <th class="num">Hours</th>
                <th class="num">The line</th>
                <th class="num">Over by</th>
              </tr>
            </thead>
            <tbody>
              ${v.map(
                (w) => html`
                  <tr>
                    <td>${mediumDate(w.week_start_date)}</td>
                    <td>${w.who}</td>
                    <td class="num">${formatHours(w.hours)}</td>
                    <td class="num">${formatHours(w.line)}</td>
                    <td class="num">${formatHours(w.excess)}</td>
                  </tr>
                `,
              )}
            </tbody>
          </table>
        `;
      default:
        return String(v);
    }
  }

  /**
   * The dismissal confirm. Dismissal is the one decision no run undoes, so it is the one that
   * asks — and the note is where the reason goes, because the next person to read the row is
   * usually not the person who decided.
   */
  _confirmRow(item) {
    const saving = this._saving.has(item.id);
    return html`
      <tr class="panel" data-confirm=${item.id}>
        <td colspan="7">
          <div class="confirm">
            <span>
              <strong>Dismiss ${item.entity_label}?</strong> No run raises it again, whether or not
              the flag is still true. Only a person puts it back.
            </span>
            <input
              type="text"
              placeholder="Why (optional, kept with the item)"
              aria-label="Why this is dismissed"
              maxlength="500"
              .value=${this._note}
              @input=${(e) => (this._note = e.target.value)}
            />
            <button
              class="danger"
              ?disabled=${saving}
              @click=${() => this._setStatus(item, 'dismissed', { note: this._note.trim() })}
            >
              ${saving ? 'Dismissing…' : 'Dismiss'}
            </button>
            <button ?disabled=${saving} @click=${() => (this._confirming = null)}>Cancel</button>
          </div>
        </td>
      </tr>
    `;
  }

  _emptyText() {
    if (this._status === 'open' && !this._filtered)
      return 'Nothing on the worklist: every flag the last run raised has been resolved or dismissed.';
    return 'No items match these filters.';
  }

  _table() {
    const list = this._list;
    const items = rankItems(list.items, list.offset ?? 0);
    const pageMax = this._pageMax(items);
    return html`
      <div class="scroll">
        <table>
          <thead>
            <tr>
              <th class="rank">#</th>
              <th>What</th>
              <th>Missing</th>
              <th class="num">Hours at stake</th>
              <th>Owner</th>
              <th>Fix</th>
              <th><span class="sr-only">Decision</span></th>
            </tr>
          </thead>
          <tbody>
            ${
              items.length === 0
                ? html`<tr>
                    <td class="state" colspan="7">${this._emptyText()}</td>
                  </tr>`
                : repeat(
                    items,
                    (i) => i.id,
                    (i) => this._row(i, pageMax),
                  )
            }
          </tbody>
        </table>
      </div>
    `;
  }

  _foot() {
    const list = this._list;
    const total = list.total ?? 0;
    const offset = list.offset ?? 0;
    const shown = list.items.length;
    const from = total === 0 ? 0 : offset + 1;
    const to = offset + shown;
    return html`
      <div class="foot">
        <span>
          ${
            total === 0
              ? 'No items'
              : `${from}–${to} of ${count(total)} · ${formatHours(list.total_hours_at_stake)} at stake`
          }
        </span>
        ${
          total > shown || offset > 0
            ? html`
                <span class="pager">
                  <button
                    ?disabled=${offset === 0}
                    @click=${() => {
                      this._offset = Math.max(0, offset - PAGE_SIZE);
                      this._load();
                    }}
                  >
                    Previous
                  </button>
                  <button
                    ?disabled=${to >= total}
                    @click=${() => {
                      this._offset = offset + PAGE_SIZE;
                      this._load();
                    }}
                  >
                    Next
                  </button>
                </span>
              `
            : nothing
        }
      </div>
    `;
  }

  get _columns() {
    // The specification is shared with the server's CSV (lib/hygiene.js).
    return withFormats(hygieneColumns(), {
      byUnit: {
        hours: (v) => formatHours(v),
        date: (v) => mediumDate(v),
      },
    });
  }

  _subtitle() {
    const list = this._list;
    if (!list)
      return 'Every missing fact the last run found, ranked by the hours it puts in doubt.';
    const open = list.counts?.by_status?.open ?? 0;
    const status =
      this._status === 'any'
        ? 'every status'
        : `${statusMeta(this._status).label.toLowerCase()} items`;
    // The order is a claim about the list, so the sentence says the order actually chosen.
    const order =
      this._sort === 'hours'
        ? 'ranked by the hours whose confidence the fix would improve'
        : `ordered by ${SORTS.find((s) => s.value === this._sort)?.label.toLowerCase()}`;
    return open === 0 && this._status === 'open'
      ? 'Nothing open: every flag the last run raised has been resolved or dismissed.'
      : `Showing ${status}, ${order}.`;
  }

  render() {
    const list = this._list;
    let body;
    if (this._error) body = html`<div class="state error">${this._error}</div>`;
    else if (!list && this._loading) body = html`<div class="state">Loading the worklist…</div>`;
    else if (!list) body = html`<div class="state">No worklist to show.</div>`;
    else
      body = html`
        ${this._table()} ${this._foot()}
        <details
          class="table"
          ?open=${this._tableOpen}
          @toggle=${(e) => (this._tableOpen = e.target.open)}
        >
          <summary>Table view: this page of the worklist</summary>
          <data-table
            caption="The worklist as the endpoint returned it, one row per item"
            export-name="data-hygiene-worklist"
            page-size="25"
            dense
            .columns=${this._columns}
            .rows=${worklistRows(list)}
          ></data-table>
        </details>
      `;

    return html`
      <div class="card" aria-busy=${this._loading ? 'true' : 'false'}>
        <div class="head">
          <div>
            <div class="eyebrow">Data Hygiene</div>
            <h2>Which missing facts are costing the forecast the most confidence?</h2>
            <div class="sub">${this._subtitle()}</div>
          </div>
          ${this._kpis()}
        </div>
        ${this._controls()} ${this._notices()} ${body}
        ${
          this._canWrite
            ? nothing
            : html`<p class="state" style="text-align: left; padding: 10px 0 0">
                Resolving and dismissing items needs the Planner role; this list is read-only for
                you.
              </p>`
        }
      </div>
    `;
  }
}

customElements.define('hygiene-worklist', HygieneWorklist);
